import type { ExtraLineItem, ShippingPayload } from '../services/api';
import { getOneTimeVariantId } from '../hooks/useDonationConfig';
import type { DonationAmount, OneTimeVariantIds } from '../hooks/useDonationConfig';
import type { ShippingDetectionSource } from '../types/config';

export interface DonationState {
  donationMode: 'recurring' | 'one-time';
  donateRecurringAmount: DonationAmount | null;
  donateOneTimeAmount: DonationAmount | null;
  donateCustomAmount: string;
  donationRecurringVariantId: string | null;
  donationOneTimeVariantIds: OneTimeVariantIds | null;
}

export const DONATION_MIN_EUR = 1;
export const DONATION_MAX_EUR = 1000;

export function parseCustomDonationAmount(raw: string, min = DONATION_MIN_EUR, max = DONATION_MAX_EUR): number | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed.length === 0) return null;
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed);
  if (rounded < min || rounded > max) return null;
  return rounded;
}

export function buildExtraLineItems(
  d: DonationState,
  limits: { minEur?: number; maxEur?: number } = {},
): ExtraLineItem[] {
  const min = limits.minEur ?? DONATION_MIN_EUR;
  const max = limits.maxEur ?? DONATION_MAX_EUR;
  const items: ExtraLineItem[] = [];

  if (d.donationMode === 'recurring' && d.donateRecurringAmount !== null && d.donationRecurringVariantId) {
    let qty: number | null;
    if (d.donateRecurringAmount === 'custom') {
      qty = parseCustomDonationAmount(d.donateCustomAmount, min, max);
    } else {
      const fixed = Number(d.donateRecurringAmount);
      qty = Number.isFinite(fixed) && fixed >= min && fixed <= max ? fixed : null;
    }
    if (qty !== null) {
      items.push({
        variant_id: d.donationRecurringVariantId,
        quantity: qty,
        kind: 'donation',
      });
    }
    return items;
  }

  if (d.donationMode === 'one-time' && d.donateOneTimeAmount !== null) {
    let variantId: string | null = null;
    let quantity = 1;
    if (d.donateOneTimeAmount === 'custom') {
      const custom = parseCustomDonationAmount(d.donateCustomAmount, min, max);
      if (custom !== null) {
        variantId = getOneTimeVariantId(d.donationOneTimeVariantIds, 'custom');
        quantity = custom;
      }
    } else {
      variantId = getOneTimeVariantId(d.donationOneTimeVariantIds, d.donateOneTimeAmount);
    }
    if (variantId) {
      items.push({ variant_id: variantId, quantity, kind: 'donation' });
    }
  }

  return items;
}

export function getDonationCheckoutAmount(
  d: Pick<DonationState, 'donationMode' | 'donateRecurringAmount' | 'donateOneTimeAmount' | 'donateCustomAmount'>,
  limits: { minEur?: number; maxEur?: number } = {},
): number {
  const min = limits.minEur ?? DONATION_MIN_EUR;
  const max = limits.maxEur ?? DONATION_MAX_EUR;
  const amount = d.donationMode === 'recurring' ? d.donateRecurringAmount : d.donateOneTimeAmount;
  if (amount === null) return 0;
  if (amount === 'custom') {
    return parseCustomDonationAmount(d.donateCustomAmount, min, max) ?? 0;
  }
  const fixed = Number(amount);
  return Number.isFinite(fixed) && fixed >= min && fixed <= max ? fixed : 0;
}

export interface ShippingResolved {
  supported: boolean;
  zone_id?: string | null;
  shipping_price: number;
}

export interface ShippingPayloadInput {
  isShippingActive: boolean;
  shippingResult: ShippingResolved | null;
  shippingCountryUI: string;
  shippingPostalCodeUI: string;
  shippingDetectionSource: ShippingDetectionSource | null;
}

export interface ShippingPayloadFragment {
  shipping?: ShippingPayload;
}

export function buildShippingPayload(i: ShippingPayloadInput): ShippingPayloadFragment {
  // An unsupported zone no longer blocks the order: the address still travels so the backend
  // records it on the cart and the store decides shipping.
  if (!i.isShippingActive || !i.shippingResult) {
    return {};
  }
  return {
    shipping: {
      country: i.shippingCountryUI,
      postal_code: i.shippingPostalCodeUI,
      zone_id: i.shippingResult.zone_id ?? null,
      price_estimated: i.shippingResult.shipping_price,
      detection_source: i.shippingDetectionSource ?? undefined,
    },
  };
}

export const ALLOWED_CHECKOUT_DOMAINS = ['myshopify.com', 'shopify.com', 'miloylola.com', 'patitasco.com', 'localhost'];

/**
 * Combina el allowlist hardcodeado con los dominios extra que el servidor
 * devuelve en `allowed_checkout_domains` (checkout client-side). Es una UNIÓN:
 * nunca es MENOS permisivo que el hardcode. Con `extra` vacío/omitido devuelve
 * solo el hardcode (compatibilidad con las llamadas de 1 argumento).
 */
function resolveAllowedDomains(extra?: string[]): string[] {
  if (!extra || extra.length === 0) return ALLOWED_CHECKOUT_DOMAINS;
  const merged = new Set(ALLOWED_CHECKOUT_DOMAINS);
  for (const domain of extra) {
    const clean = String(domain || '')
      .trim()
      .toLowerCase();
    if (clean) merged.add(clean);
  }
  return Array.from(merged);
}

export function isAllowedCheckoutHostname(hostname: string, allowedDomains?: string[]): boolean {
  const domains = resolveAllowedDomains(allowedDomains);
  return domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

export function validateCheckoutUrl(url: string, allowedDomains?: string[]): string | null {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    return null;
  }
  if (!isAllowedCheckoutHostname(hostname, allowedDomains)) return null;
  return hostname;
}
